import React, { useState, useEffect, useCallback } from "react";
import { FaShieldAlt, FaSync, FaDownload, FaExternalLinkAlt, FaExclamationTriangle, FaBan } from "react-icons/fa";
import api from "../utils/api";
import { isHighSimilarity } from "../utils/plagiarismPolicy";

function statusLabel(status) {
  switch (status) {
    case "completed":
      return "Completed";
    case "processing":
      return "Processing…";
    case "failed":
      return "Failed";
    case "not_configured":
      return "Not configured";
    case "pending":
    default:
      return "Pending";
  }
}

function statusClass(status) {
  switch (status) {
    case "completed":
      return "bg-green-900 text-green-200";
    case "processing":
      return "bg-blue-900 text-blue-200";
    case "failed":
      return "bg-red-900 text-red-200";
    case "not_configured":
      return "bg-gray-700 text-gray-300";
    default:
      return "bg-yellow-900 text-yellow-200";
  }
}

export default function PlagiarismCheckPanel({ research, onUpdated, policy: policyProp, approvalBlock: approvalBlockProp }) {
  const [check, setCheck] = useState(research?.plagiarismCheck || {});
  const [configured, setConfigured] = useState(true);
  const [policy, setPolicy] = useState(policyProp || null);
  const [approvalBlock, setApprovalBlock] = useState(approvalBlockProp || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setCheck(research?.plagiarismCheck || {});
  }, [research]);

  useEffect(() => {
    if (policyProp) setPolicy(policyProp);
  }, [policyProp]);

  useEffect(() => {
    if (approvalBlockProp) setApprovalBlock(approvalBlockProp);
  }, [approvalBlockProp]);

  const refreshStatus = useCallback(async () => {
    if (!research?._id) return;
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get(`/research/${research._id}/plagiarism-status`);
      setConfigured(data.configured !== false);
      setCheck(data.plagiarismCheck || {});
      if (data.policy) setPolicy(data.policy);
      if (data.approvalBlock) setApprovalBlock(data.approvalBlock);
      onUpdated?.();
    } catch (err) {
      setError(err.response?.data?.error || "Could not refresh plagiarism status");
    } finally {
      setLoading(false);
    }
  }, [research?._id, onUpdated]);

  const runCheck = async () => {
    if (!research?._id) return;
    setLoading(true);
    setError("");
    try {
      await api.post(`/research/${research._id}/plagiarism-check`);
      setCheck((prev) => ({ ...prev, status: "processing" }));
    } catch (err) {
      setError(err.response?.data?.error || "Failed to start plagiarism check");
    } finally {
      setLoading(false);
    }
  };

  const downloadReport = async () => {
    try {
      const response = await api.get(`/research/${research._id}/plagiarism-report`, {
        responseType: "blob"
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `plagiarism-report-${research._id}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      if (check.reportUrl) {
        window.open(check.reportUrl, "_blank", "noopener,noreferrer");
        return;
      }
      setError("Report is not ready yet. Try refreshing status in a few minutes.");
    }
  };

  const threshold = policy?.maxSimilarityForApproval ?? 25;
  const similarity = check.similarityPercent;
  const highSimilarity = isHighSimilarity(similarity, threshold);
  const blocksApproval = approvalBlock?.blocked;

  return (
    <div className="mb-6 rounded-lg border border-gray-600 bg-gray-800/80 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <h3 className="font-semibold flex items-center gap-2">
          <FaShieldAlt className="text-indigo-400" />
          DrillBit Plagiarism Check
        </h3>
        <span className={`px-2 py-1 rounded text-xs font-medium ${statusClass(check.status)}`}>
          {statusLabel(check.status)}
        </span>
      </div>

      {!configured && (
        <p className="text-sm text-gray-400 mb-3">
          DrillBit credentials are not set on the server. Add{" "}
          <code className="text-xs bg-gray-700 px-1 rounded">DRILLBIT_*</code> variables to enable checks.
        </p>
      )}

      {policy?.enforcementEnabled && (
        <p className="text-xs text-gray-500 mb-3">
          Approval policy: completed check required
          {policy.enforceSimilarityThreshold ? `; similarity must be below ${threshold}%` : ""}.
        </p>
      )}

      {check.status === "completed" && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3 text-sm">
          <div className="bg-gray-700 rounded p-3">
            <p className="text-gray-400 text-xs mb-1">Similarity</p>
            <p className={`text-lg font-semibold ${highSimilarity ? "text-red-400" : "text-green-400"}`}>
              {similarity != null ? `${similarity}%` : "—"}
            </p>
          </div>
          {check.aiPercent != null && (
            <div className="bg-gray-700 rounded p-3">
              <p className="text-gray-400 text-xs mb-1">AI content</p>
              <p className="text-lg font-semibold text-white">{check.aiPercent}%</p>
            </div>
          )}
          {check.grammarScore != null && (
            <div className="bg-gray-700 rounded p-3">
              <p className="text-gray-400 text-xs mb-1">Grammar</p>
              <p className="text-lg font-semibold text-white">{check.grammarScore}</p>
            </div>
          )}
        </div>
      )}

      {blocksApproval && (
        <div className="flex items-start gap-2 text-red-300 text-sm mb-3 bg-red-950/50 border border-red-800 rounded p-2">
          <FaBan className="shrink-0 mt-0.5" />
          <span>{approvalBlock.message}</span>
        </div>
      )}

      {highSimilarity && !blocksApproval && (
        <div className="flex items-start gap-2 text-amber-300 text-sm mb-3 bg-amber-950/40 border border-amber-800 rounded p-2">
          <FaExclamationTriangle className="shrink-0 mt-0.5" />
          <span>
            Similarity is {similarity}% (at or above {threshold}%). Review the DrillBit report before approving.
          </span>
        </div>
      )}

      {check.status === "processing" && (
        <p className="text-sm text-gray-400 mb-3">
          DrillBit is analyzing this PDF (usually 10–15 minutes). Refresh status or wait for the webhook callback.
        </p>
      )}

      {check.error && (
        <p className="text-sm text-red-400 mb-3">{check.error}</p>
      )}

      {check.completedAt && (
        <p className="text-xs text-gray-500 mb-3">
          Completed: {new Date(check.completedAt).toLocaleString()}
        </p>
      )}

      <div className="flex flex-wrap gap-2">
        {configured && (
          <>
            <button
              type="button"
              onClick={runCheck}
              disabled={loading || check.status === "processing"}
              className="bg-indigo-600 px-3 py-1.5 rounded text-sm hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-1"
            >
              <FaShieldAlt />
              {check.paperId ? "Re-run check" : "Run check"}
            </button>
            <button
              type="button"
              onClick={refreshStatus}
              disabled={loading}
              className="bg-gray-600 px-3 py-1.5 rounded text-sm hover:bg-gray-500 disabled:opacity-50 flex items-center gap-1"
            >
              <FaSync className={loading ? "animate-spin" : ""} />
              Refresh status
            </button>
          </>
        )}
        {(check.status === "completed" && (check.dKey || check.reportUrl)) && (
          <>
            <button
              type="button"
              onClick={downloadReport}
              className="bg-blue-600 px-3 py-1.5 rounded text-sm hover:bg-blue-700 flex items-center gap-1"
            >
              <FaDownload />
              Download report
            </button>
            {check.reportUrl && (
              <a
                href={check.reportUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-gray-600 px-3 py-1.5 rounded text-sm hover:bg-gray-500 flex items-center gap-1"
              >
                <FaExternalLinkAlt />
                Open in DrillBit
              </a>
            )}
          </>
        )}
      </div>

      {error && <p className="text-red-400 text-sm mt-2">{error}</p>}
    </div>
  );
}
